import React from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

export type ConfirmableStatus = "waiting" | "ongoing" | "completed";

const STATUS_COPY: Record<
  ConfirmableStatus,
  { title: string; message: string; confirmLabel: string; confirmClass: string }
> = {
  waiting: {
    title: "Mark as waiting?",
    message: "This appointment will be marked as waiting.",
    confirmLabel: "Waiting",
    confirmClass:
      "bg-violet-600 hover:bg-violet-700 focus:ring-violet-500/30",
  },
  ongoing: {
    title: "Mark as ongoing?",
    message: "This appointment will be marked as ongoing.",
    confirmLabel: "Ongoing",
    confirmClass: "bg-primary hover:bg-primary/90 focus:ring-primary/30",
  },
  completed: {
    title: "Mark as completed?",
    message: "This appointment will be marked as completed.",
    confirmLabel: "Completed",
    confirmClass:
      "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500/30",
  },
};

type Props = {
  open: boolean;
  status: ConfirmableStatus | null;
  patientName?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function StatusConfirmModal({
  open,
  status,
  patientName,
  onCancel,
  onConfirm,
}: Props) {
  if (!open || !status) return null;

  const copy = STATUS_COPY[status];

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="status-confirm-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div>
            <h2
              id="status-confirm-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {copy.title}
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {patientName
                ? `${copy.message.replace("This appointment", patientName + "'s appointment")}`
                : copy.message}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 ${copy.confirmClass}`}
          >
            {copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
